import https from 'https';
import { PreloadJob } from '../../UnicronApp/preloader/PreloadJob';
import { makePreloadSlot } from '../../UnicronApp/preloader/PreloadSlot';
import { LoggerService } from '../services/LoggerService/LoggerService';
import { Fetch } from '../services/NetworkService';
import { env } from './env';

const isHttpErrorCode = (it: number) => it >= 500 && it <= 599;

export const proxyToHost = async (job: PreloadJob, host: string, protocol: string) => {
  const { server, services } = job;
  const { cache, logger, network } = services;
  const { fetch } = network;

  if (server) {
    cache.set(ProxyInfoSlot, true);

    const { req, res } = server;

    if (env.SKIP_PROXYING) {
      res.writeHead(200, { 'Content-Type': 'text/plain' });
      res.end(`Skipped proxying request for ${req.url}`);
      return;
    }

    // Override HTTPS agent when we want to ignore self-signed certificates in development.
    const fetchInitAgent =
      env.HTTPS_REJECT_UNAUTHORIZED === '0'
        ? {
            agent: new https.Agent({
              rejectUnauthorized: false,
            }),
          }
        : {};

    const fullUrl = `${protocol}://${host}${req.url}`;

    const fetched = await logger.profile(`Fetching header from ${host}`, () => {
      try {
        const cookie = req.headers.cookie === undefined ? [] : { cookie: req.headers.cookie };
        return fetch(fullUrl, {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore Type definition for fetch does not have agent property. May only work when node-fetch is being used.
          ...fetchInitAgent,
          headers: { ...services.tracingHeaders, ...cookie },
        });
      } catch (error) {
        /**
         * We do not need to await this async call, since its only purpose is to gather and log
         * more data, which can happen after the response has been sent.
         */
        void debugRedirectLoop(error, fetch, logger, fullUrl);
        throw error;
      }
    });

    if (isHttpErrorCode(fetched.status)) {
      logger.error(`Got a ${fetched.status} from ${host}`);
    }

    res.writeHead(fetched.status, {
      ...Object.fromEntries(fetched.headers.entries()),
      'content-encoding': '',
      'x-via': 'Unicron',
    });

    const body = await logger.profile(`Fetching body from ${host}`, () => fetched.text());

    res.end(body);
  }
};

export const ProxyInfoSlot = makePreloadSlot('HAS_PROXIED', () => false);

const debugRedirectLoop = async (
  error: unknown,
  fetch: Fetch,
  logger: LoggerService,
  url: string,
) => {
  if (!/maximum redirect/i.test(String(error))) return;

  const log = (info: unknown) => logger.error(`Debugging proxied redirect loop: ${info}`, { info });

  try {
    const firstFetched = await fetch(url, { redirect: 'manual' });
    const firstLocation = firstFetched.headers.get('location');
    log(`First fetch gave status ${firstFetched.status} with location header "${firstLocation}"`);

    if (!firstLocation) return;

    const secondFetched = await fetch(firstLocation, { redirect: 'manual' });
    const secondLocation = secondFetched.headers.get('location');

    log(
      `Second fetch gave status ${secondFetched.status} with location header "${secondLocation}"`,
    );
  } catch (error) {
    log(error);
  }
};
